import { UserAuthProviderResponse, UserProviderProfile } from "recoil/types";
import { createBatchedAnalyticsSetter } from "@bto/shared";
import {
  getDetailedProviderData,
  getSanitizedProviderData,
} from "../utils/contextUtils";
import { getBaseUrl } from "../utils/requestUtils";

export const BatchedSetAnalyticsUser = createBatchedAnalyticsSetter<
  [
    UserAuthProviderResponse | undefined,
    UserProviderProfile | undefined,
    string | undefined,
  ]
>((providerInfo, profileInfo, distinct_id, properties) => {
  SetAnalyticsUser(providerInfo, profileInfo, distinct_id, properties);
});

export const TrackAnalyticsEvent = async (
  event_name: string,
  token?: string,
  distinct_id?: string,
  properties?: { [key: string]: string | number },
): Promise<Response | undefined> => {
  if (!token && !distinct_id) {
    console.error(
      `At least one of token or distinct_id must be provided for event '${event_name}'`,
    );
    return;
  }

  const payload: {
    properties?: { [key: string]: string | number };
    distinct_id?: string;
    event_name: string;
  } = { event_name };

  if (properties) {
    payload.properties = properties;
  }

  if (distinct_id) {
    payload.distinct_id = distinct_id;
  }

  let headers: { "Content-Type": string; Authorization?: string } = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers = { ...headers, Authorization: `Bearer ${token}` };
  }

  try {
    const baseUrl = await getBaseUrl();
    const response = await fetch(`${baseUrl}/api/v1/analytics/events/`, {
      body: JSON.stringify(payload),
      method: "POST",
      headers,
    });
    return response;
  } catch (e) {
    console.error(`Error sending analytics event: '${e}'`);
    return undefined;
  }
};

export const SetAnalyticsUser = async (
  providerInfo?: UserAuthProviderResponse,
  profileInfo?: UserProviderProfile,
  distinct_id?: string,
  properties?: { [key: string]: string | number },
): Promise<Response | undefined> => {
  if (!providerInfo && !distinct_id) {
    console.error("Either auth info or distinct_id must be provided");
    return;
  }

  const payload: {
    properties?: { [key: string]: string | number };
    distinct_id?: string;
  } = {};

  if (properties) {
    payload.properties = {
      ...payload.properties,
      ...properties,
    };
  }

  if (providerInfo) {
    const userContext = getSanitizedProviderData(providerInfo);
    payload.properties = {
      ...payload.properties,
      ...userContext,
    };
  }

  if (profileInfo) {
    const profileContext = getDetailedProviderData(profileInfo);
    payload.properties = {
      ...payload.properties,
      ...profileContext,
    };
  }

  if (distinct_id) {
    payload.distinct_id = distinct_id;
  }

  let headers: { "Content-Type": string; Authorization?: string } = {
    "Content-Type": "application/json",
  };

  if (providerInfo?.token) {
    headers = {
      ...headers,
      Authorization: `Bearer ${providerInfo.token}`,
    };
  }

  try {
    const baseUrl = await getBaseUrl();
    const response = await fetch(`${baseUrl}/api/v1/analytics/users/`, {
      body: JSON.stringify(payload),
      method: "POST",
      headers,
    });
    return response;
  } catch (e) {
    console.error(`Error setting analytics user: '${e}'`);
    return undefined;
  }
};
